import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { Send, X, User } from 'lucide-react';

interface ChatBoxProps {
  rideId: string;
  onClose: () => void;
}

const API_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000') + '/api/v1';

const ChatBox: React.FC<ChatBoxProps> = ({ rideId, onClose }) => {
  const [messages, setMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => {
    loadMessages();
    const interval = setInterval(loadMessages, 3000);
    return () => clearInterval(interval);
  }, [rideId]);

  const loadMessages = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const res = await fetch(`${API_URL}/rides/${rideId}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setMessages(data);
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 100);
    } catch (e) {}
  };

  const QUICK_REPLIES = ["I'm here", 'Where are you?', 'Coming out now', 'Please wait a moment', 'Almost there'];

  const sendMessage = async (text?: string) => {
    const body = (text ?? newMessage).trim();
    if (!body) return;
    try {
      const token = localStorage.getItem('accessToken');
      await fetch(`${API_URL}/rides/${rideId}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ message: body }),
      });
      setNewMessage('');
      loadMessages();
    } catch (e) {}
  };

  return (
    <motion.div
      initial={{ y: '100%' }}
      animate={{ y: 0 }}
      exit={{ y: '100%' }}
      className="fixed bottom-0 left-0 right-0 bg-safaricab-dark rounded-t-3xl z-50 h-[60vh] flex flex-col"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <h3 className="text-white font-bold">Chat</h3>
        <button onClick={onClose} className="text-white/50"><X className="w-5 h-5" /></button>
      </div>
      <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
        {messages.map((msg) => (
          <div key={msg.id} className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}>
            <div className={`max-w-[80%] rounded-2xl px-4 py-2 ${msg.sender_id === user.id ? 'bg-safaricab-gold text-black' : 'bg-safaricab-card text-white'}`}>
              {msg.sender_id !== user.id && <p className="text-safaricab-gold text-xs font-bold mb-1">{msg.sender?.full_name || 'User'}</p>}
              <p className="text-sm">{msg.message}</p>
            </div>
          </div>
        ))}
        <div ref={messagesEndRef} />
      </div>
      <div className="px-4 pt-2 pb-1 flex flex-col items-end gap-1.5 max-h-44 overflow-y-auto no-scrollbar">
        {QUICK_REPLIES.map((q) => (
          <button key={q} onClick={() => sendMessage(q)} className="bg-safaricab-card border border-white/10 text-white/80 text-sm rounded-full px-4 py-2">
            {q}
          </button>
        ))}
      </div>
      <div className="px-4 pt-3 border-t border-white/10 flex items-center gap-2" style={{ paddingBottom: 'calc(0.75rem + var(--sai-bottom))' }}>
        <input
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
          placeholder="Type a message..."
          className="flex-1 bg-safaricab-card border border-white/10 rounded-xl px-4 py-2.5 text-white outline-none"
        />
        <button onClick={() => sendMessage()} className="bg-safaricab-gold text-black p-2.5 rounded-xl">
          <Send className="w-5 h-5" />
        </button>
      </div>
    </motion.div>
  );
};

export default ChatBox;
